import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const PathwaySelector = ({ onPathwaySelect }) => {
  const [selectedPathway, setSelectedPathway] = useState(null);

  const pathways = [
    {
      id: 'executive',
      title: 'Executive Strategy Session',
      subtitle: 'For C-Suite & Senior Leadership',
      description: 'Strategic consultation focused on AI transformation roadmap, competitive positioning, and organizational readiness.',
      duration: '60 minutes',
      icon: 'Crown',
      features: [
        'AI survival strategy assessment',
        'Competitive advantage analysis',
        'Executive briefing materials',
        'Custom transformation roadmap'
      ],
      cta: 'Book Executive Session',
      popular: true
    },
    {
      id: 'technical',
      title: 'Technical Deep Dive',
      subtitle: 'For CTOs & Engineering Leaders',
      description: 'Technical consultation covering architecture, implementation strategies, and open-source AI stack evaluation.',
      duration: '90 minutes',
      icon: 'Code',
      features: [
        'Technical architecture review',
        'Open-source stack evaluation',
        'Implementation methodology',
        'Security & compliance guidance'
      ],
      cta: 'Schedule Technical Review'
    },
    {
      id: 'implementation',
      title: 'Implementation Planning',
      subtitle: 'For Project & Operations Teams',
      description: 'Practical consultation focused on execution planning, resource allocation, and timeline development.',
      duration: '45 minutes',
      icon: 'Settings',
      features: [
        'Project scope definition',
        'Resource requirement analysis',
        'Timeline & milestone planning',
        'Risk mitigation strategies'
      ],
      cta: 'Plan Implementation'
    }
  ];

  const handlePathwaySelect = (pathway) => {
    setSelectedPathway(pathway?.id);
    onPathwaySelect(pathway);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            Choose Your Engagement Pathway
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Select the consultation type that best matches your role and organizational needs. Each pathway is designed to deliver maximum value for your specific situation.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {pathways?.map((pathway) => (
            <div
              key={pathway?.id}
              className={`relative bg-white rounded-2xl border-2 transition-all duration-300 hover:shadow-executive-lg ${
                selectedPathway === pathway?.id
                  ? 'border-brand-primary shadow-executive-lg'
                  : 'border-gray-200 hover:border-brand-primary/50'
              }`}
            >
              {pathway?.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-brand-accent text-gray-900 px-4 py-2 rounded-full text-sm font-semibold">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-brand-primary/10 rounded-lg flex items-center justify-center mr-4">
                    <Icon name={pathway?.icon} size={24} className="text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-brand-primary">{pathway?.title}</h3>
                    <p className="text-sm text-text-secondary">{pathway?.subtitle}</p>
                  </div>
                </div>

                <p className="text-text-secondary mb-6 leading-relaxed">
                  {pathway?.description}
                </p>

                <div className="flex items-center mb-6 text-sm text-text-secondary">
                  <Icon name="Clock" size={16} className="mr-2" />
                  {pathway?.duration}
                </div>

                <div className="space-y-3 mb-8">
                  {pathway?.features?.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <Icon name="Check" size={16} className="text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-sm text-text-primary">{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={selectedPathway === pathway?.id ? "default" : "outline"}
                  fullWidth
                  className={selectedPathway === pathway?.id ? "bg-brand-primary" : ""}
                  iconName="ArrowRight"
                  iconPosition="right"
                  onClick={() => handlePathwaySelect(pathway)}
                >
                  {pathway?.cta}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-text-secondary mb-4">
            Not sure which pathway is right for you?
          </p>
          <Button
            variant="ghost"
            className="text-brand-primary hover:text-brand-primary/80"
            iconName="MessageCircle"
            iconPosition="left"
          >
            Chat with our AI Consultant
          </Button>
        </div>
      </div>
    </section>
  );
};

export default PathwaySelector;